<template>
  <aside v-if="visible" class="ae-overlay" aria-label="Agent">
    <AgentConversation
      ref="conversation"
      :entry="entry"
      :context="context"
      :storage-key="storageKey"
      compact
      @open-full="openFull"
      @close="overlay.open = false"
    />
  </aside>
</template>

<script>
import axios from 'axios';
import AgentConversation from './AgentConversation.vue';
import './agent.css';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import { sessionEntry, agentOverlay } from '@/services/agentSessionController';
import { canvasContext, canvasPageContext } from '@/services/canvasContext';

export default {
  name: 'AgentOverlay',
  components: { AgentConversation },
  data() {
    return { overlay: agentOverlay, lastPage: '', pageTimer: null };
  },
  computed: {
    allowed() {
      const user = this.$store.state.auth.current_user;
      return !!user && !!user.is_superuser && canAccessFeature(FEATURES.MODULE_AGENT);
    },
    onAgentPage() {
      return this.$route.name === 'Beta-Agent';
    },
    visible() {
      return this.overlay.open && this.allowed && !this.onAgentPage;
    },
    scope() {
      return `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}`;
    },
    entry() {
      return sessionEntry(`${this.scope}:overlay`);
    },
    storageKey() {
      return `agent-overlay:${this.scope}`;
    },
    page() {
      const r = this.$route;
      const ent = this.$store.state.entity || {};
      const ten = this.$store.state.tenant || {};
      const title = String(document.title || '').replace(/\s*\|\s*Kairon Health\s*$/, '');
      const ids = r.params.entityIds;
      const routeEntity = r.params.entityId || r.params.entity_id || (Array.isArray(ids) ? ids[0] : ids) || null;
      const entity = routeEntity
        ? { id: routeEntity, name: String(routeEntity) === String(ent.current_entity_id) ? (ent.current_entity_name || '') : '' }
        : null;
      return {
        route: r.name || '', path: r.fullPath || r.path || '', title,
        tenant: ten.current_tenant_name || ten.current_tenant_slug || '',
        params: { ...(r.params || {}) }, query: { ...(r.query || {}) },
        entity,
        // The Workflow Builder graph, when one is on screen. Route ids alone
        // could not tell the agent what the user is looking at, so questions
        // about the canvas ("why is this join empty?") had no referent.
        // Shape only — names, roles, columns, joins; never row data.
        canvas: canvasPageContext(),
      };
    },
    sessionLive() {
      return ['launching', 'ready', 'thinking', 'rate_limited'].includes(this.entry.state.connState);
    },
    // Cheap change signal for the canvas graph. Watching canvasContext deeply
    // would fire on every pointer move during a node drag; this only changes
    // when the SHAPE does.
    canvasStamp() {
      if (!canvasContext.active) return '';
      return JSON.stringify(canvasPageContext());
    },
    context() {
      const r = this.$route;
      return {
        metricId: r.params.metricId || r.query.metric || null,
        studyId: r.params.studyId || r.query.study || null,
        s3Key: r.query.file || '',
        schemaId: r.query.schema || null,
      };
    },
  },
  watch: {
    visible(on) {
      if (on) this.entry.view = this;
      else if (this.entry.view === this) this.entry.view = null;
    },
    onAgentPage(on) {
      if (on) this.overlay.open = false;
    },
    '$route.fullPath'() {
      this.tellPage();
    },
    // A canvas edit does not change the route, so without this the agent's
    // picture of the graph would freeze at whatever it was on arrival. The
    // 600ms debounce in tellPage plus its stamp check keep a drag from
    // flooding the session with context turns.
    canvasStamp() {
      this.tellPage();
    },
    sessionLive(live) {
      if (live) this.tellPage(true);
    },
  },
  beforeUnmount() {
    if (this.entry.view === this) this.entry.view = null;
  },
  methods: {
    openFull(sessionId) {
      const { href } = this.$router.resolve({ name: 'Beta-Agent', params: sessionId ? { sessionId } : {} });
      window.open(href, '_blank', 'noopener');
    },
    tellPage(force = false) {
      if (!this.sessionLive || !this.entry.sessionId || this.onAgentPage) return;
      clearTimeout(this.pageTimer);
      this.pageTimer = setTimeout(() => {
        const page = this.page;
        const stamp = JSON.stringify(page);
        if (!force && stamp === this.lastPage) return;
        this.lastPage = stamp;
        const conv = this.$refs.conversation;
        if (conv) conv.sendPageContext(page);
        else axios.post(`/api/impact_analytics/agent-sessions/${this.entry.sessionId}/turn/`, { type: 'page_context', page }).catch(() => {});
      }, 600);
    },
    scrollChat() {
      if (this.$refs.conversation) this.$refs.conversation.scrollChat();
    },
    scrollArtifacts() {},
    startRunPolling() {},
    stopRunPolling() {},
    fetchRunPlots() {},
  },
};
</script>
